export const privacyPolicy = {
  lastUpdated: "January 2026",
  companyName: "Flash",
  companyAddress: "Gudalur, Tamil Nadu, India",
  sections: [
    {
      title: "1. Introduction",
      content: `Welcome to Flash ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and services.

By using Flash, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our Service.`
    },
    {
      title: "2. Information We Collect",
      content: `We collect several types of information to provide and improve our Service:

Personal Information:
• Name and phone number (for account creation)
• Delivery addresses (for order fulfillment)
• Location data (to determine service availability)
• Payment information (transaction records only)

Automatically Collected Information:
• Device information (model, operating system, unique identifiers)
• Usage data (app interactions, features used, time spent)
• Log data (IP address, browser type, access times)
• Location data (GPS coordinates when using the app)

Order Information:
• Products ordered and quantities
• Order history and preferences
• Delivery instructions and notes
• Feedback and ratings`
    },
    {
      title: "3. How We Use Your Information",
      content: `We use the collected information for various purposes:

Service Delivery:
• Process and fulfill your orders
• Coordinate delivery with local stores and delivery partners
• Send order confirmations and updates
• Provide customer support

Service Improvement:
• Analyze usage patterns to improve user experience
• Develop new features and services
• Conduct research and analytics
• Optimize delivery routes and times

Communication:
• Send promotional offers and updates (with your consent)
• Notify you about service changes
• Respond to your inquiries and requests
• Send important service announcements

Legal and Security:
• Comply with legal obligations
• Prevent fraud and unauthorized access
• Enforce our Terms and Conditions
• Protect our rights and property`
    },
    {
      title: "4. Data Sharing and Disclosure",
      content: `We may share your information with:

Service Providers:
• Local stores and merchants (order details only)
• Delivery partners (name, phone, delivery address)
• Payment processors (transaction information)
• Cloud storage providers (encrypted data)
• Analytics services (anonymized data)

Legal Requirements:
• Law enforcement or government agencies (when required by law)
• Legal proceedings or investigations
• Protection of our rights and safety

Business Transfers:
• In case of merger, acquisition, or sale of assets
• Your data may be transferred to the new entity

We do NOT:
• Sell your personal information to third parties
• Share your data for marketing purposes without consent
• Disclose sensitive information unnecessarily`
    },
    {
      title: "5. Data Security",
      content: `We implement appropriate security measures to protect your information:

Technical Measures:
• Encryption of data in transit and at rest
• Secure servers with regular security updates
• Firewall protection and intrusion detection
• Regular security audits and assessments

Organizational Measures:
• Limited access to personal data (need-to-know basis)
• Employee training on data protection
• Confidentiality agreements with partners
• Incident response procedures

However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.`
    },
    {
      title: "6. Data Retention",
      content: `We retain your information for as long as necessary to:

• Maintain your account and provide services
• Comply with legal obligations (tax, accounting)
• Resolve disputes and enforce agreements
• Improve our services and analytics

Retention Periods:
• Account information: Until account deletion + 90 days
• Order history: 3 years for accounting purposes
• Support communications: 2 years
• Analytics data: Anonymized and retained indefinitely
• Deleted account data: Permanently removed after 90 days

You can request deletion of your data at any time by contacting support.`
    },
    {
      title: "7. Your Privacy Rights",
      content: `You have the following rights regarding your personal information:

Access and Portability:
• Request a copy of your personal data
• Download your order history and account information
• Receive data in a structured, machine-readable format

Correction and Update:
• Update your name, phone number, and addresses
• Correct inaccurate or incomplete information
• Modify your preferences and settings

Deletion:
• Request deletion of your account and data
• Remove specific addresses or information
• Opt-out of promotional communications

Restriction and Objection:
• Limit how we use your data
• Object to data processing for certain purposes
• Withdraw consent for optional data collection

To exercise these rights, contact us via WhatsApp or email. We will respond within 30 days.`
    },
    {
      title: "8. Location Data",
      content: `We collect and use location data to:

• Determine service availability in your area
• Calculate delivery times and fees
• Optimize delivery routes
• Show nearby stores and products
• Improve service coverage

Location Permissions:
• You can enable or disable location services in your device settings
• Disabling location may limit some features
• We only collect location when the app is in use
• Location data is not shared with third parties for marketing

You have full control over location permissions and can change them at any time.`
    },
    {
      title: "9. Cookies and Tracking",
      content: `We use cookies and similar tracking technologies to:

• Remember your preferences and settings
• Analyze app usage and performance
• Provide personalized content and recommendations
• Improve security and prevent fraud

Types of Tracking:
• Essential cookies (required for app functionality)
• Analytics cookies (usage statistics)
• Preference cookies (remember your choices)

You can manage cookie preferences in your device settings. Disabling certain cookies may affect app functionality.`
    },
    {
      title: "10. Third-Party Services",
      content: `Our app may contain links to third-party services:

• Social media platforms (Instagram, WhatsApp)
• Payment gateways
• Map services
• Analytics providers

We are not responsible for:
• Privacy practices of third-party services
• Content or security of external websites
• Data collection by third parties

We recommend reviewing the privacy policies of any third-party services you use.`
    },
    {
      title: "11. Children's Privacy",
      content: `Our Service is not intended for children under 18 years of age.

• We do not knowingly collect information from children
• If we discover we have collected data from a child, we will delete it immediately
• Parents or guardians should supervise children's use of devices
• Contact us if you believe we have collected information from a child

If you are under 18, please do not use our Service or provide any personal information.`
    },
    {
      title: "12. Data Transfers",
      content: `Your information may be transferred to and maintained on servers located outside your state, province, or country.

• Data may be stored in cloud servers in different locations
• We ensure appropriate safeguards are in place
• Transfers comply with applicable data protection laws
• Your consent to this Privacy Policy represents your agreement to such transfers`
    },
    {
      title: "13. Changes to Privacy Policy",
      content: `We may update this Privacy Policy from time to time to reflect:

• Changes in our practices
• Legal or regulatory requirements
• New features or services
• User feedback and improvements

Notification of Changes:
• Updated policy will be posted in the app
• "Last Updated" date will be revised
• Significant changes will be notified via email or in-app notification
• Continued use after changes constitutes acceptance

We encourage you to review this policy periodically.`
    },
    {
      title: "14. Data Protection Officer",
      content: `For privacy-related inquiries, you can contact our Data Protection team:

Email: privacy@flash.delivery
WhatsApp: +91 91234 56789
Address: Flash, Gudalur, Tamil Nadu, India

We will respond to your inquiries within 30 days and work to resolve any concerns.`
    },
    {
      title: "15. Compliance and Certifications",
      content: `We comply with:

• Information Technology Act, 2000 (India)
• Personal Data Protection Bill (India)
• Industry best practices for data security
• Payment Card Industry Data Security Standard (PCI DSS)

We regularly review and update our practices to ensure compliance with evolving regulations.`
    },
    {
      title: "16. Contact Us",
      content: `If you have questions or concerns about this Privacy Policy or our data practices:

Flash
Gudalur, Tamil Nadu, India

Support Hours: 6:00 AM - 10:00 PM (Daily)
WhatsApp: +91 91234 56789
Instagram: @flash.delivery.in
Email: support@flash.delivery

We value your privacy and are committed to protecting your personal information.`
    }
  ]
};

// Made with Bob
